import { useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Box,
  Button,
  Collapse,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import type { Promisable } from "type-fest";

import {
  approverPartyForAssessor,
  displayName,
  instrumentVersionLabel,
  isInvolvementLive,
  openedWhereRequired,
  progressOf,
  type Assessment,
  type AssessmentId,
  type CurrentUser,
  type DeleteAssessmentRequest,
  type PersonInvolvement,
} from "@thalamos/haven-common";

import api, { apiHooks } from "../../api/index.js";
import type { AssessmentFact } from "../../components/AssessmentFacts/AssessmentFacts.js";
import {
  DeleteAssessmentDialog,
  type DeletionOutcome,
} from "../../components/DeleteAssessmentDialog/DeleteAssessmentDialog.js";
import { ReturnedForChanges } from "../../components/ReturnedForChanges/ReturnedForChanges.js";
import {
  approverNeeded,
  SubmitAssessmentDialog,
  type Approver,
  type Submission,
  type SubmitOutcome,
  type SubmitTrack,
} from "../../components/SubmitAssessmentDialog/SubmitAssessmentDialog.js";
import { useUser } from "../../Context/UserContext.js";
import { createRoute } from "../../routing/createRoute.js";
import {
  assessorNameOf,
  deciderNameOf,
  offersDelete,
} from "../Persons/involvementManagement.js";
import { useAssessment, type Opening } from "./AssessmentContext.js";
import { latestSubmission } from "./latestSubmission.js";
import { overviewDomainsOf, requiredItemsDoneOf } from "./overview_domains.js";
import { OverviewDomainRow, type OverviewLayout } from "./OverviewDomainRow.js";
import { submissionRequestOf } from "./submissionRequestOf.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });
const CALENDAR_DATE_TIME = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "long",
  timeStyle: "short",
});

/**
 * The overview of an assessment being worked on: its key facts, every domain
 * with its progress, and the actions that move it on.
 */
export const AssessmentOverview = () => {
  const { assessment } = useAssessment();

  return assessment === undefined ? null : (
    <AssessmentOverviewFor assessment={assessment} />
  );
};

const AssessmentOverviewFor = ({ assessment }: { assessment: Assessment }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { opening, flushNow } = useAssessment();
  const [{ data: person, response: personResponse }] = apiHooks.person.byId(
    assessment.personId,
  );
  const [{ data: involvements, response }, rereadInvolvements] =
    apiHooks.involvement.forPerson(assessment.personId);

  const submit = async ({
    assessedOn,
    approverUserId,
  }: Submission): Promise<SubmitOutcome> => {
    if (opening.kind !== "open") {
      return "failed";
    }

    // Submitting deletes the draft, so a save still waiting would fail after it.
    await flushNow();

    const request = submissionRequestOf({
      opened: opening.opened,
      assessment,
      assessedOn,
      approverUserId,
      t,
    });

    // A request that cannot be built is never sent, so it fails rather than
    // being refused: nothing changed underneath it to read again.
    if (request === undefined) {
      return "failed";
    }

    const sent = await api.formDraft
      .submit(assessment.id, request)
      .catch(() => undefined);

    if (sent?.data !== undefined) {
      navigate(createRoute.person(assessment.personId));
      return "submitted";
    }

    // Submit is offered only once the browser's own judgement finds nothing
    // missing, so a refusal is something that changed underneath it, such as
    // the assessment being submitted elsewhere; the overview does not relay
    // the server's reasons. A 5xx rejects, per the client's `validateStatus`,
    // and is a failure to send.
    if (sent === undefined) {
      return "failed";
    }

    // A refusal may be the Approver changing; reading who approves again lets
    // the dialog tell.
    // TODO(assessments): a re-read answered with a 4xx clears the
    // involvements, and the dialog then reports the Approver as gone.
    if (assessment.definitive) {
      try {
        await rereadInvolvements();
      } catch {
        // The involvements as last read stand.
      }
    }

    return "refused";
  };

  const deleteAssessment = async ({
    assessmentId,
    request,
  }: {
    assessmentId: AssessmentId;
    request: DeleteAssessmentRequest;
  }): Promise<DeletionOutcome> => {
    const deleted = await api.assessment.delete(assessmentId, request);

    if (deleted.status !== 204) {
      return "failed";
    }

    navigate(createRoute.person(assessment.personId));
    return "deleted";
  };

  return (
    <AssessmentOverviewContent
      assessment={assessment}
      opening={opening}
      involvements={response?.status === 200 ? involvements : undefined}
      viewer={user}
      personName={
        personResponse?.status === 200 && person !== undefined
          ? displayName(person.names)
          : undefined
      }
      onSubmit={submit}
      onDelete={deleteAssessment}
    />
  );
};

/**
 * The overview's rendering, with everything it shows injected so a story or a
 * test can show any state of it without an API client.
 *
 * An assessment that is no longer open never shows its actions. One being
 * revised after a return keeps why it came back in view.
 */
export const AssessmentOverviewContent = ({
  assessment,
  opening,
  involvements,
  viewer,
  personName,
  onSubmit,
  onDelete,
}: {
  assessment: Assessment;
  opening: Opening;
  /** Who is involved with the young person; undefined until read. */
  involvements: PersonInvolvement[] | undefined;
  viewer: CurrentUser | undefined;
  /** The young person's name, which completing one is worded around; undefined until read. */
  personName: string | undefined;
  onSubmit: (submission: Submission) => Promisable<SubmitOutcome>;
  onDelete: (deletion: {
    assessmentId: AssessmentId;
    request: DeleteAssessmentRequest;
  }) => Promisable<DeletionOutcome>;
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<Assessment | null>(null);
  // Undefined until somebody expands or collapses a domain.
  const [expandedByChoice, setExpandedByChoice] = useState<
    ReadonlySet<string> | undefined
  >();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const theme = useTheme();
  const layout: OverviewLayout = useMediaQuery(theme.breakpoints.down("md"))
    ? "narrow"
    : "wide";

  const isOwner = viewer?.id === assessment.ownerUserId;
  const latest = latestSubmission(assessment);
  // Shown until the next submission, which carries no decision yet.
  const returned =
    latest?.decision?.outcome === "returned" ? latest.decision : undefined;
  const whyReturned =
    returned === undefined ? null : (
      <ReturnedForChanges
        reason={returned.reason}
        approverName={deciderNameOf(involvements, returned.decidedByUserId)}
        returnedAt={returned.decidedAt}
      />
    );

  if (assessment.status !== "open") {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Alert severity="info">{t("assessment.overview.notOpen")}</Alert>
        <Link
          component={RouterLink}
          to={createRoute.person(assessment.personId)}
        >
          {t("assessment.overview.toPersonPage")}
        </Link>
      </Stack>
    );
  }

  if (opening.kind === "loading") {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("common.loading")}
      </Typography>
    );
  }

  if (opening.kind === "failed") {
    return (
      <Alert severity="error">
        {t(`assessment.page.failed.${opening.reason}`)}
      </Alert>
    );
  }

  const { schema, form, draft } = opening.opened;
  const progress = progressOf({
    schema,
    form,
    document: draft.document,
    context: draft.context,
    opened: openedWhereRequired(draft),
  });

  if (progress.isErr()) {
    return (
      <Alert severity="error">{t("assessment.page.failed.unavailable")}</Alert>
    );
  }

  const { groups, completed, included, ready } = progress.value;
  const domains = overviewDomainsOf({
    opened: opening.opened,
    progress: progress.value,
  });
  const actioned = requiredItemsDoneOf(progress.value);
  // Until somebody chooses, the first domain under way or missing a comment
  // is open.
  const expandedWhenWide =
    expandedByChoice ??
    new Set(
      domains
        .filter(
          ({ state, missingComments }) =>
            state === "in_progress" || missingComments > 0,
        )
        .slice(0, 1)
        .map(({ group }) => group),
    );
  // One at a time on a phone, so the list stays short enough to scan. Derived
  // rather than stored, so a wide screen that narrows obeys it too.
  const expanded: ReadonlySet<string> =
    layout === "narrow"
      ? new Set(
          domains
            .map(({ group }) => group)
            .filter((group) => expandedWhenWide.has(group))
            .slice(0, 1),
        )
      : expandedWhenWide;
  const toggle = (group: string) => {
    setExpandedByChoice(() => {
      if (layout === "narrow") {
        return new Set(expanded.has(group) ? [] : [group]);
      }

      const next = new Set(expanded);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };
  const allExpanded =
    domains.length > 0 && domains.every(({ group }) => expanded.has(group));

  const { definitive } = assessment;
  const approverParty = approverPartyForAssessor(
    involvements ?? [],
    assessment.ownerUserId,
  );
  const approver: Approver | undefined =
    approverParty?.kind === "user"
      ? { userId: approverParty.userId, name: approverParty.name }
      : undefined;
  const caseManagerName = (involvements ?? []).find(
    (involvement) =>
      involvement.kind === "responsible_user" && isInvolvementLive(involvement),
  )?.party.name;
  // Undefined until it can be worded: completing one names the young person.
  const track: SubmitTrack | undefined = definitive
    ? { kind: "approval", approver, caseManagerName }
    : personName === undefined
      ? undefined
      : { kind: "completion", personName };
  const finishing = definitive ? "approval" : "completion";
  const left = included - completed;
  const toOpen = groups.filter(
    ({ included: counts, state }) => counts && state === "not_opened",
  ).length;
  const missing = [
    ...(ready
      ? []
      : [
          toOpen > 0
            ? t(`assessment.overview.domainsToOpen.${finishing}`, {
                left: toOpen,
              })
            : left > 0
              ? t(`assessment.overview.domainsLeft.${finishing}`, { left })
              : t(`assessment.overview.notReady.${finishing}`),
        ]),
    ...(definitive && approver === undefined
      ? [approverNeeded(t, caseManagerName)]
      : []),
  ];
  const assessor = assessorNameOf(assessment, viewer, involvements);
  const facts = [
    {
      label: t("assessment.overview.assessor"),
      value: assessor ?? t("assessment.overview.assessorUnknown"),
    },
    ...(definitive
      ? [
          {
            label: t("assessment.overview.approver"),
            value: approver?.name ?? t("assessment.overview.noApprover"),
          },
        ]
      : []),
    {
      label: t("assessment.overview.started"),
      value: CALENDAR_DATE.format(assessment.startedAt),
    },
    {
      label: t("assessment.overview.lastActivity"),
      value: CALENDAR_DATE_TIME.format(draft.savedAt),
    },
    {
      label: t("assessment.overview.instrumentVersion"),
      value: instrumentVersionLabel(assessment.instrument.name),
    },
  ];
  // Its owner's to delete here; whoever runs the case does so from the
  // profile.
  const narrowAction = { flex: "1 1 0", minHeight: 44 };
  const deleteButton =
    isOwner && offersDelete(viewer, involvements)(assessment) ? (
      <Button
        variant="outlined"
        color="error"
        // Left off on a phone, where each action has half the width.
        startIcon={layout === "wide" ? <DeleteOutlineIcon /> : undefined}
        onClick={() => {
          setDeleting(assessment);
        }}
        sx={layout === "narrow" ? narrowAction : undefined}
      >
        {t("assessment.overview.delete")}
      </Button>
    ) : null;
  // Who approves the assessment is unknown until the involvements are read,
  // so nothing is offered on a guess.
  const submittable =
    involvements !== undefined && missing.length === 0 && track !== undefined;
  const submitButton = isOwner ? (
    <Button
      variant="contained"
      disabled={!submittable}
      onClick={() => {
        setSubmitting(true);
      }}
      sx={layout === "narrow" ? narrowAction : undefined}
    >
      {t(
        definitive
          ? "assessment.overview.submitForApproval"
          : "assessment.overview.submit",
      )}
    </Button>
  ) : null;
  const reasons =
    isOwner && missing.length > 0 ? (
      <Stack>
        {missing.map((reason) => (
          <Typography key={reason} variant="body2" color="text.secondary">
            {reason}
          </Typography>
        ))}
      </Stack>
    ) : null;
  const progressLine = (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontVariantNumeric: "tabular-nums" }}
    >
      <Box component="span" sx={{ fontWeight: 700, color: "primary.main" }}>
        {t(
          layout === "narrow"
            ? "assessment.overview.domainsCompleteShort"
            : "assessment.overview.domainsComplete",
          { completed, included },
        )}
      </Box>
      {" · "}
      {t(
        layout === "narrow"
          ? "assessment.overview.itemsActionedOfShort"
          : "assessment.overview.itemsActionedOf",
        actioned,
      )}
    </Typography>
  );
  const readyPill = submittable ? (
    <Box
      role="status"
      sx={(theme) => ({
        alignSelf: "flex-start",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        minHeight: 24,
        px: 1.25,
        borderRadius: 3,
        fontSize: 12,
        fontWeight: 600,
        bgcolor: alpha(theme.palette.success.main, 0.12),
        color: "success.dark",
      })}
    >
      <CheckIcon sx={{ fontSize: 14 }} aria-hidden />
      {t(`assessment.overview.ready.${finishing}`)}
    </Box>
  ) : null;
  const overallBar = (
    <LinearProgress
      variant="determinate"
      value={
        actioned.total === 0
          ? 0
          : Math.round((actioned.done / actioned.total) * 100)
      }
      aria-hidden
      sx={{ height: 6, borderRadius: 3 }}
    />
  );
  const carryOverRefused =
    opening.opened.carryOverRefused === true ? (
      <Alert severity="info">{t("assessment.overview.carryOverRefused")}</Alert>
    ) : null;
  const domainList =
    groups.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {t("assessment.overview.noDomains")}
      </Typography>
    ) : (
      <Paper
        variant="outlined"
        component="ul"
        aria-label={t("assessment.overview.domains")}
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          ...(layout === "narrow"
            ? { borderRadius: 0, borderLeft: 0, borderRight: 0 }
            : {}),
        }}
      >
        {domains.map((domain) => (
          <OverviewDomainRow
            key={domain.group}
            assessmentId={assessment.id}
            domain={domain}
            expanded={expanded.has(domain.group)}
            layout={layout}
            onToggle={() => {
              toggle(domain.group);
            }}
          />
        ))}
      </Paper>
    );

  return (
    <>
      {layout === "wide" ? (
        <Stack spacing={3} sx={{ p: 3 }}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                useFlexGap
                spacing={2}
              >
                <Box sx={{ flex: "1 1 360px", minWidth: 0 }}>
                  <FactList facts={facts} layout={layout} />
                </Box>
                <Stack direction="row" spacing={1.5} useFlexGap>
                  {deleteButton}
                  {submitButton}
                </Stack>
              </Stack>
              {reasons === null ? null : (
                <Box sx={{ alignSelf: "flex-end", textAlign: "right" }}>
                  {reasons}
                </Box>
              )}
            </Stack>
          </Paper>
          {whyReturned}
          {carryOverRefused}
          <Stack spacing={1}>
            {progressLine}
            {readyPill}
            {overallBar}
          </Stack>
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
            >
              <Typography variant="h5" component="h2">
                {t("assessment.overview.domains")}
              </Typography>
              {domains.length === 0 ? null : (
                <Button
                  size="small"
                  onClick={() => {
                    setExpandedByChoice(
                      new Set(
                        allExpanded ? [] : domains.map(({ group }) => group),
                      ),
                    );
                  }}
                >
                  {t(
                    allExpanded
                      ? "assessment.overview.collapseAll"
                      : "assessment.overview.expandAll",
                  )}
                </Button>
              )}
            </Stack>
            {domainList}
          </Stack>
        </Stack>
      ) : (
        <Stack>
          <Stack
            spacing={1.25}
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: "grey.100",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Button
              size="small"
              onClick={() => {
                setDetailsOpen((was) => !was);
              }}
              aria-expanded={detailsOpen}
              aria-controls="overview-record-details"
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transition: "transform 150ms",
                    transform: detailsOpen ? "rotate(180deg)" : "none",
                  }}
                />
              }
              sx={{ alignSelf: "flex-start", ml: -1 }}
            >
              {t("assessment.overview.details")}
            </Button>
            <Collapse in={detailsOpen} id="overview-record-details">
              <Paper variant="outlined" sx={{ px: 1.75, py: 1.5 }}>
                <FactList facts={facts} layout={layout} />
              </Paper>
            </Collapse>
            {whyReturned}
            {carryOverRefused}
            {deleteButton === null && submitButton === null ? null : (
              <Stack direction="row" spacing={1.25}>
                {deleteButton}
                {submitButton}
              </Stack>
            )}
            {progressLine}
            {readyPill ?? reasons}
            {overallBar}
          </Stack>
          <Box sx={{ mt: 1 }}>{domainList}</Box>
        </Stack>
      )}

      {submitting && track !== undefined ? (
        <SubmitAssessmentDialog
          track={track}
          onClose={() => {
            setSubmitting(false);
          }}
          onSubmit={onSubmit}
        />
      ) : null}
      <DeleteAssessmentDialog
        assessment={deleting}
        assessorName={assessor}
        personName={personName}
        // The owner's own screen has already worked out how far it has got.
        readProgress={() => ({ kind: "read", completed, included })}
        onClose={() => {
          setDeleting(null);
        }}
        onDelete={onDelete}
      />
    </>
  );
};

/** The record's facts, a label over each value: in a row across the page, or two to a line on a phone. */
const FactList = ({
  facts,
  layout,
}: {
  facts: readonly AssessmentFact[];
  layout: OverviewLayout;
}) => (
  <Box
    component="dl"
    sx={{
      m: 0,
      display: layout === "wide" ? "flex" : "grid",
      flexWrap: "wrap",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: layout === "wide" ? "8px 32px" : "8px 20px",
    }}
  >
    {facts.map(({ label, value }) => (
      <Box key={label} sx={{ minWidth: 0 }}>
        <Typography
          component="dt"
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700 }}
        >
          {label}
        </Typography>
        <Typography
          component="dd"
          variant="body2"
          sx={{ m: 0, overflowWrap: "anywhere" }}
        >
          {value}
        </Typography>
      </Box>
    ))}
  </Box>
);
